package dev.lingvo.ops.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.components.OpsLoad
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.state.logFilterSummary
import dev.lingvo.ops.state.rememberOpsPaging
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.shared.ops.OpsLogRowDto
import dev.lingvo.shared.ops.OpsLogScope
import dev.lingvo.shared.ops.OpsLogSource
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LDateRange
import dev.lingvo.site.ds.LFilterBar
import dev.lingvo.site.ds.LFilterField
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import org.jetbrains.compose.web.dom.Text

private data class LogQuery(val scope: OpsLogScope, val action: String, val since: String, val until: String, val cursor: String?)

private val ACTIONS = listOf(
    LSelectOption("", "Any action"),
    LSelectOption("CREDITS_GRANTED", "Credits granted"),
    LSelectOption("MT_JOB_FAILED", "Run failed by hand"),
    LSelectOption("CRON_JOB_RUN", "Scheduled job run by hand"),
    LSelectOption("CREDENTIALS_REVOKED", "Credentials ended"),
    LSelectOption("PLAN_COMPED", "Plan comped"),
    LSelectOption("COMP_ENDED", "Comp ended"),
    LSelectOption("TICKET_REPLIED", "Ticket replied"),
    LSelectOption("TICKET_STATUS_CHANGED", "Ticket status changed"),
    LSelectOption("TICKET_LINKED", "Ticket linked"),
)

@Page("/log")
@Composable
fun LogPage() {
    var scope by remember { mutableStateOf(OpsLogScope.ALL) }
    var action by remember { mutableStateOf("") }
    var since by remember { mutableStateOf("") }
    var until by remember { mutableStateOf("") }
    OpsShell("Log") {
        val paging = rememberOpsPaging<OpsLogRowDto>(listOf(scope.name, action, since, until), pageSize = 50)
        val resource = rememberOpsResource(LogQuery(scope, action, since, until, paging.cursor)) { query ->
            OpsApi.log(null, query.action.takeIf(String::isNotBlank), query.scope.name, query.since.takeIf(String::isNotBlank), query.until.takeIf(String::isNotBlank), query.cursor)
        }
        val reads = rememberOpsResource(Unit) { OpsApi.readDays() }
        fun refresh() { resource.refresh(); reads.refresh() }
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            OpsPageHeader("Operator log", "Review recorded operator actions and coarse read activity. Workspace actions also appear in the customer audit log.", resource.state.isRefreshing || reads.state.isRefreshing, ::refresh)
            LFilterBar(
                fields = listOf(
                    LFilterField("log-scope", "Scope", scope.name.takeIf { scope != OpsLogScope.ALL }, { scope = OpsLogScope.ALL }) { modifier ->
                        LSegmented(
                            options = listOf(
                                LSegmentedOption(OpsLogScope.WORKSPACE, "Workspace actions"),
                                LSegmentedOption(OpsLogScope.PLATFORM, "Platform actions"),
                                LSegmentedOption(OpsLogScope.ALL, "All"),
                            ), selected = scope, onSelected = { scope = it }, label = "Which actions", modifier = modifier,
                        )
                    },
                    LFilterField("log-action", "Action", action.takeIf(String::isNotBlank), { action = "" }) { modifier ->
                        LSelect(action, ACTIONS, { action = it }, modifier)
                    },
                    LFilterField("log-range", "Dates", listOf(since, until).filter(String::isNotBlank).joinToString(" to ").takeIf(String::isNotBlank), { since = ""; until = "" }) { modifier ->
                        LDateRange(since, until, { from, to -> since = from; until = to }, modifier)
                    },
                ),
                onClearAll = { scope = OpsLogScope.ALL; action = ""; since = ""; until = "" },
                resultSummary = logFilterSummary(null, action, scope, since, until, (resource.state.load as? OpsLoad.Ready)?.value?.rows?.size),
            )
            OpsPanel("Actions", "Keyset-paged at 50 rows. Platform actions have no workspace; this view does not establish customer notification.") {
                OpsResourceStates(resource.state, "Nothing yet", "Nothing has been done from this console yet. Every write will appear here.", filtered = "No action matches these filters.".takeIf { scope != OpsLogScope.ALL || action.isNotBlank() || since.isNotBlank() || until.isNotBlank() }, retry = resource::refresh) { page ->
                    LTable(caption = "What Lingvo did") {
                        LTableHead { LTableRow { listOf("When", "Operator", "Action", "Target", "What happened", "Detail").forEach { LTableHeaderCell { Text(it) } } } }
                        LTableBody { page.rows.forEach { row -> LogRow(row) } }
                    }
                }
                OpsResourcePagination(paging, resource.state, "actions")
            }
            OpsPanel("Reads", "Reads are rolled up per operator and UTC day; they are not individually audited.") {
                OpsResourceStates(reads.state, "No reads recorded", "No operator has read anything through this console yet.", retry = reads::refresh) { window ->
                    LTable(caption = "Operator reads by day") {
                        LTableHead { LTableRow { listOf("Day", "Operator", "Reads", "Workspaces", "Threshold").forEach { LTableHeaderCell { Text(it) } } } }
                        LTableBody { window.rows.forEach { row ->
                            LTableRow {
                                LTableCell { Text(row.day) }; LTableCell { Text(row.operator) }
                                LTableCell { Text(formatCount(row.reads)) }; LTableCell { Text(formatCount(row.workspaces)) }
                                LTableCell { LBadge(formatCount(row.burstThreshold), variant = if (row.overThreshold) LBadgeVariant.DANGER else LBadgeVariant.NEUTRAL) }
                            }
                        } }
                    }
                }
            }
        }
    }
}

@Composable
private fun LogRow(row: OpsLogRowDto) {
    LTableRow {
        LTableCell { Text(formatUtc(row.at)) }
        LTableCell { Text(row.operator) }
        LTableCell { LBadge(row.action, variant = if (row.source == OpsLogSource.UNCLAIMED) LBadgeVariant.DANGER else LBadgeVariant.NEUTRAL) }
        LTableCell { Text(row.orgLabel ?: "Platform") }
        LTableCell { Text(row.sentence) }
        LTableCell { Text(row.facts.joinToString(" · ") { "${it.label}: ${it.value}" } + if (row.reason != null) " · Why, internally: ${row.reason}" else "") }
    }
}
